import { parseWorkspace, type Workspace } from "./workspace"

/**
 * Guest-mode persistence: browser localStorage only. Nothing leaves the device.
 * Phase 1b swaps/extends this with a server-backed store for signed-in users;
 * keep this interface small so that is a drop-in.
 */
const KEY = "aab.workspaces.v1"

function read(): Workspace[] {
  if (typeof window === "undefined") return []
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return []
    return arr.map(parseWorkspace).filter((w): w is Workspace => w !== null)
  } catch {
    return []
  }
}

function write(list: Workspace[]): boolean {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list))
    return true
  } catch {
    return false
  }
}

export function listWorkspaces(): Workspace[] {
  return read().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export function getWorkspace(id: string): Workspace | null {
  return read().find((w) => w.id === id) ?? null
}

export function saveWorkspace(w: Workspace): boolean {
  const list = read().filter((x) => x.id !== w.id)
  list.push(w)
  return write(list)
}

export function deleteWorkspace(id: string): boolean {
  return write(read().filter((w) => w.id !== id))
}

export function storageAvailable(): boolean {
  try {
    const k = "__aab_test__"
    window.localStorage.setItem(k, "1")
    window.localStorage.removeItem(k)
    return true
  } catch {
    return false
  }
}

const ACK_KEY = "aab.disclaimer.v1"
export function hasAcknowledgedDisclaimer(): boolean {
  try { return window.localStorage.getItem(ACK_KEY) === "1" } catch { return false }
}
export function acknowledgeDisclaimer(): void {
  try { window.localStorage.setItem(ACK_KEY, "1") } catch { /* ignore */ }
}

/** Remove everything this site keeps in the browser: incidents, the intake draft, and the disclaimer note. */
export function clearAllLocalData(): boolean {
  try {
    for (const k of [KEY, "aab.draft.v1", ACK_KEY]) window.localStorage.removeItem(k)
    return true
  } catch {
    return false
  }
}
